/**
 * Guide Me - locator engine.
 *
 * Resolution priority: testid > role+name > label > text > selector.
 *
 * Designed so plugins generally do not need to be retrofitted: most actionable
 * elements in the EmpowerID Identity Fabric plugins are already exposed via `aria-label`,
 * surrounding `<label>` text, or visible button text.
 */

import type { Locator, LocatorRoleName } from './types';

/* -------------------------------------------------------------------------- */
/* Helpers                                                                     */
/* -------------------------------------------------------------------------- */

function trim(text: string | null | undefined): string {
  return (text ?? '').replace(/\s+/g, ' ').trim();
}

function matches(value: string | null | undefined, expected: string, exact: boolean): boolean {
  const v = trim(value);
  const e = trim(expected);
  if (!e) return false;
  return exact ? v === e : v.includes(e);
}

/** Read the accessible name of an element using a small subset of the AccName algorithm. */
export function accessibleName(el: Element): string {
  if (!(el instanceof HTMLElement)) return trim(el.textContent);
  const aria = el.getAttribute('aria-label');
  if (aria) return trim(aria);

  const labelledby = el.getAttribute('aria-labelledby');
  if (labelledby) {
    const ids = labelledby.split(/\s+/).filter(Boolean);
    const parts: string[] = [];
    for (const id of ids) {
      const ref = el.ownerDocument?.getElementById(id);
      if (ref) parts.push(trim(ref.textContent));
    }
    if (parts.length > 0) return trim(parts.join(' '));
  }

  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
    if (el.id) {
      const lbl = el.ownerDocument?.querySelector(`label[for="${CSS.escape(el.id)}"]`);
      if (lbl) return trim(lbl.textContent);
    }
    const wrappingLabel = el.closest('label');
    if (wrappingLabel) return trim(wrappingLabel.textContent);
    if (el instanceof HTMLInputElement && (el.type === 'submit' || el.type === 'button')) {
      return trim(el.value);
    }
  }

  if (el.tagName === 'IMG') {
    const alt = el.getAttribute('alt');
    if (alt) return trim(alt);
  }

  const title = el.getAttribute('title');
  if (title) return trim(title);

  return trim(el.textContent);
}

const TAG_TO_ROLE: Readonly<Record<string, LocatorRoleName>> = {
  BUTTON: 'button',
  A: 'link',
  TEXTAREA: 'textbox',
  SELECT: 'combobox',
  TABLE: 'table',
  TR: 'row',
  TD: 'cell',
  TH: 'columnheader',
  UL: 'list',
  OL: 'list',
  LI: 'listitem',
  IMG: 'img',
};

/** Roles that are structural containers — resolving them should return the
 *  container itself, never an inner interactive descendant. */
const STRUCTURAL_ROLES = new Set<LocatorRoleName>([
  'table', 'grid', 'row', 'cell', 'columnheader', 'rowheader',
  'list', 'listitem', 'dialog', 'alertdialog', 'alert', 'status', 'img',
]);

/** Compute the implicit or explicit ARIA role of an element. */
export function roleOf(el: Element): LocatorRoleName | null {
  if (!(el instanceof HTMLElement)) return null;
  const explicit = el.getAttribute('role');
  if (explicit) return explicit as LocatorRoleName;

  if (el.tagName === 'INPUT') {
    const type = (el as HTMLInputElement).type;
    if (type === 'checkbox') return 'checkbox';
    if (type === 'radio') return 'radio';
    if (type === 'search') return 'searchbox';
    if (type === 'submit' || type === 'button' || type === 'reset') return 'button';
    return 'textbox';
  }
  if (/^H[1-6]$/.test(el.tagName)) return 'heading';
  return TAG_TO_ROLE[el.tagName] ?? null;
}

/** Walk up to find a "user-facing" element when a query landed on a wrapper.
 *  Structural container elements (table, list, dialog…) are returned as-is
 *  so move/scrollTo steps highlight the whole container rather than unwrapping
 *  to the first interactive child inside it. */
function actionable(el: Element): HTMLElement {
  if (el instanceof HTMLElement) {
    const role = roleOf(el);
    if (role && STRUCTURAL_ROLES.has(role)) return el;
    if (el.matches('button, a, input, textarea, select, [role], [tabindex]')) return el;
    const inner = el.querySelector<HTMLElement>(
      'button, a, input, textarea, select, [role="button"], [role="link"], [role="textbox"], [role="searchbox"], [role="combobox"], [role="checkbox"], [role="radio"], [role="switch"], [role="menuitem"], [role="option"], [role="tab"]',
    );
    if (inner) return inner;
    return el;
  }
  return (el as HTMLElement);
}

/* -------------------------------------------------------------------------- */
/* Strategies                                                                  */
/* -------------------------------------------------------------------------- */

function escapeAttrValue(value: string): string {
  // CSS is not always present on the global (older jsdom, restricted runtimes).
  if (typeof CSS !== 'undefined' && typeof (CSS as { escape?: (s: string) => string }).escape === 'function') {
    return (CSS as { escape: (s: string) => string }).escape(value);
  }
  // Minimal fallback - escape backslash and double-quote inside an attribute value.
  return value.replace(/[\\"]/g, '\\$&');
}

function findByTestId(root: ParentNode, testid: string, exact: boolean): Element[] {
  if (exact) {
    return Array.from(root.querySelectorAll(`[data-testid="${escapeAttrValue(testid)}"]`));
  }
  return Array.from(root.querySelectorAll('[data-testid]')).filter(
    (el) => trim((el as HTMLElement).dataset.testid).includes(testid),
  );
}

function findByRoleAndName(
  root: ParentNode,
  role: LocatorRoleName,
  name: string | undefined,
  exact: boolean,
): Element[] {
  const all = Array.from(root.querySelectorAll<HTMLElement>('*'));
  return all.filter((el) => {
    const r = roleOf(el);
    if (r !== role) return false;
    if (name === undefined) return true;
    return matches(accessibleName(el), name, exact);
  });
}

function findByLabel(root: ParentNode, label: string, exact: boolean): Element[] {
  // Strategy 1: <label for="id"> referencing inputs.
  const labels = Array.from(root.querySelectorAll('label'));
  const matchedInputs: Element[] = [];
  for (const lbl of labels) {
    if (!matches(lbl.textContent, label, exact)) continue;
    const forId = lbl.getAttribute('for');
    if (forId) {
      const input = lbl.ownerDocument?.getElementById(forId);
      if (input) matchedInputs.push(input);
    } else {
      const innerInput = lbl.querySelector<HTMLElement>('input, textarea, select');
      if (innerInput) matchedInputs.push(innerInput);
    }
  }
  if (matchedInputs.length > 0) return matchedInputs;

  // Strategy 2: aria-label exact / substring on focusable controls.
  const ariaMatches = Array.from(root.querySelectorAll<HTMLElement>('[aria-label]')).filter(
    (el) => matches(el.getAttribute('aria-label'), label, exact),
  );
  if (ariaMatches.length > 0) return ariaMatches;

  // Strategy 3: previous sibling div.label-style text immediately above an input.
  const candidates: Element[] = [];
  const labelLikeElements = Array.from(
    root.querySelectorAll<HTMLElement>('label, span, div, p'),
  );
  for (const node of labelLikeElements) {
    if (!matches(node.textContent, label, exact)) continue;
    const sibling = node.nextElementSibling?.querySelector('input, textarea, select') ?? node.nextElementSibling;
    if (sibling instanceof HTMLElement && /input|textarea|select/i.test(sibling.tagName)) {
      candidates.push(sibling);
    }
  }
  return candidates;
}

function findByText(root: ParentNode, text: string, exact: boolean): Element[] {
  const all = Array.from(root.querySelectorAll<HTMLElement>(
    'button, a, [role="button"], [role="link"], [role="menuitem"], [role="option"], [role="tab"], summary, label, h1, h2, h3, h4, h5, h6, [role="heading"]'
  ));
  return all.filter((el) => matches(accessibleName(el), text, exact));
}

function findBySelector(root: ParentNode, selector: string): Element[] {
  try {
    return Array.from(root.querySelectorAll(selector));
  } catch {
    return [];
  }
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                  */
/* -------------------------------------------------------------------------- */

export interface ResolveOptions {
  /** Document or sub-tree to search inside. Defaults to `document`. */
  root?: ParentNode;
}

/** Resolve a locator to all matching elements, filtered to visible ones. */
export function resolveAll(locator: Locator, options: ResolveOptions = {}): HTMLElement[] {
  const explicitRoot = options.root;
  let root: ParentNode = explicitRoot ?? (typeof document !== 'undefined' ? document : (null as unknown as ParentNode));
  if (!root) return [];

  if (locator.within) {
    const parent = resolveOne(locator.within, options);
    if (!parent) return [];
    root = parent;
  }

  const exact = locator.exact !== false;
  let candidates: Element[] = [];

  // Priority: testid > role+name > label > text > selector.
  if (locator.testid) {
    candidates = findByTestId(root, locator.testid, exact);
  }
  if (candidates.length === 0 && locator.role) {
    candidates = findByRoleAndName(root, locator.role, locator.name, exact);
  }
  if (candidates.length === 0 && locator.label) {
    candidates = findByLabel(root, locator.label, exact);
  }
  if (candidates.length === 0 && locator.text) {
    candidates = findByText(root, locator.text, exact);
  }
  if (candidates.length === 0 && locator.selector) {
    candidates = findBySelector(root, locator.selector);
  }

  const visible = candidates
    .map((el) => actionable(el))
    .filter((el) => isVisible(el));
  return visible;
}

/** Resolve a locator to the first matching visible element, or null. */
export function resolveOne(locator: Locator, options: ResolveOptions = {}): HTMLElement | null {
  const all = resolveAll(locator, options);
  if (all.length === 0) return null;
  const idx = locator.nth ?? 0;
  return all[idx] ?? null;
}

/**
 * Best-effort visibility check.
 *
 * Trusts explicit hidden signals (`hidden`, `aria-hidden`, `display: none`,
 * `visibility: hidden`, `opacity: 0`). In real browsers it additionally rejects
 * zero-sized elements; in test runtimes without a layout engine (jsdom) the
 * 0x0 case is treated as "no info" and not rejected, so the locator engine
 * stays testable in vitest.
 */
export function isVisible(el: Element | null | undefined): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false;
  if (!el.isConnected) return false;
  if (el.hidden) return false;
  if (el.getAttribute('aria-hidden') === 'true') return false;
  const view = el.ownerDocument?.defaultView;
  const style = view?.getComputedStyle?.(el);
  if (style && (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0')) return false;

  const userAgent = (view?.navigator?.userAgent ?? '').toLowerCase();
  const isJsdom = userAgent.includes('jsdom');
  if (isJsdom) return true;

  const rect = el.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) {
    return el.getClientRects && el.getClientRects().length > 0;
  }
  return true;
}

/**
 * Wait until a locator resolves or the timeout elapses.
 * Returns the resolved element, or null on timeout.
 */
export function waitForLocator(
  locator: Locator,
  timeoutMs: number,
  options: ResolveOptions = {},
): Promise<HTMLElement | null> {
  return new Promise((resolve) => {
    const found = resolveOne(locator, options);
    if (found) {
      resolve(found);
      return;
    }
    if (typeof window === 'undefined' || typeof MutationObserver === 'undefined') {
      resolve(null);
      return;
    }

    let settled = false;
    const finish = (value: HTMLElement | null) => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      window.clearTimeout(timer);
      resolve(value);
    };

    const observer = new MutationObserver(() => {
      const el = resolveOne(locator, options);
      if (el) finish(el);
    });
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-testid', 'aria-label', 'aria-labelledby', 'role', 'hidden', 'aria-hidden', 'class'],
    });

    const timer = window.setTimeout(() => finish(null), Math.max(0, timeoutMs));
  });
}

/**
 * Describe a locator for log messages and subtitle fallbacks.
 */
export function describeLocator(locator: Locator): string {
  if (locator.testid) return `[data-testid=${locator.testid}]`;
  if (locator.role && locator.name) return `${locator.role} "${locator.name}"`;
  if (locator.role) return `role:${locator.role}`;
  if (locator.label) return `label:"${locator.label}"`;
  if (locator.text) return `text:"${locator.text}"`;
  if (locator.selector) return `css:${locator.selector}`;
  return '<empty locator>';
}
